import { createSlice } from '@reduxjs/toolkit';
import { getDisplayName } from '../../utils/userDisplay.js';
// Import all thunks from modular projectDraftThunks folder
import {
  saveDraftToStorage,
  loadDraftFromStorage,
  submitTaskComment,
  toggleCommentLike,
  updateTaskThunk,
  deleteTaskThunk,
} from './projectDraftThunks/index.js';

const initialState = {
  // Post form data
  postData: {
    title: '',
    issue: '',
    location: '',
    actionTag: null,
    content: '',
    scale: '',
    issueStatus: '',
    link: '',
    relatedSolution: null
  },
  // Project builder data
  project: {
    projectTitle: '',
    projectTitleInitialized: false,
    mission: '',
    imageUrl: '',
    solutionType: '',
    sections: [], // { id, title, body }
    phases: [], // { id, title }

    // Source tracking for different creation paths
    sourceType: null, // 'scratch' | 'post' | 'solution'
    sourceData: {
      // For 'post' source (FromPost path)
      originalPostId: null,
      selectedSolutionId: null,
      projectMission: '',

      // For 'solution' source (FromSolution path)
      sourceSolutionId: null,
      implementationPlan: ''
    }
  },

  // Team composition
  teamMembers: [],

  // Location search state
  locationSearch: {
    searchTerm: '',
    selectedLocation: null,
    isSearching: false,
    error: ''
  },

  // User search state
  userSearch: {
    query: '',
    results: [],
    isSearching: false,
    showDropdown: false,
    recentlyAdded: []
  },

  // UI state
  ui: {
    dropdownOpen: {
      issue: false,
      actionTag: false,
      issueStatus: false
    },
    confirmDialog: null,
    currentStep: 'post' // 'post' | 'team' | 'complete'
  },

  // Meta state
  isDraft: false,
  firestoreDraftId: null, // Tracks Firestore draft document ID
  lastSaved: null,
  loading: false,
  error: null
};

const projectDraftSlice = createSlice({
  name: 'projectDraft',
  initialState,
  reducers: {
    // Post form actions
    updatePostData: (state, action) => {
      state.postData = { ...state.postData, ...action.payload };
      state.isDraft = true;
    },

    setPostField: (state, action) => {
      const { field, value } = action.payload;
      state.postData[field] = value;
      state.isDraft = true;
    },
    // Project builder field
    setProjectField: (state, action) => {
      const { field, value } = action.payload;
      state.project[field] = value;
      state.isDraft = true;
    },

    // Sections CRUD
    addProjectSection: (state, action) => {
      const { prepend, ...section } = action.payload; // { id, title, body, readonly?, prepend? }
      if (prepend) {
        state.project.sections.unshift(section);
      } else {
        state.project.sections.push(section);
      }
      state.isDraft = true;
    },
    updateProjectSection: (state, action) => {
      const { id, updates } = action.payload;
      const idx = state.project.sections.findIndex(s => s.id === id);
      if (idx !== -1) {
        state.project.sections[idx] = { ...state.project.sections[idx], ...updates };
        state.isDraft = true;
      }
    },
    removeProjectSection: (state, action) => {
      const id = action.payload;
      state.project.sections = state.project.sections.filter(s => s.id !== id);
      state.isDraft = true;
    },

    // Phases CRUD
    addProjectPhase: (state, action) => {
      const phase = action.payload; // { id, objective?, tasks? }
      const normalized = {
        id: phase.id,
        objective: phase.objective || '',
        readonly: !!phase.readonly,
        // Default to an empty task list; tasks will be added explicitly via a form
        tasks: Array.isArray(phase.tasks)
          ? phase.tasks.map(t => ({
            id: t.id,
            title: t.title || '',
            content: t.content || '',
            createdBy: t.createdBy || null, // Track task creator
            assignedTo: t.assignedTo || [], // Track task assignees
            readonly: !!t.readonly,
            comments: t.comments || [] // Initialize comments array
          }))
          : []
      };
      state.project.phases.push(normalized);
      state.isDraft = true;
    },
    updateProjectPhase: (state, action) => {
      const { id, updates } = action.payload;
      const idx = state.project.phases.findIndex(p => p.id === id);
      if (idx !== -1) {
        state.project.phases[idx] = { ...state.project.phases[idx], ...updates };
        state.isDraft = true;
      }
    },

    // Task update and delete
    updateTaskLocal: (state, action) => {
      const { phaseId, taskId, updates } = action.payload;
      const phase = state.project.phases.find(p => p.id === phaseId);
      if (phase) {
        const taskIndex = phase.tasks.findIndex(t => t.id === taskId);
        if (taskIndex !== -1) {
          phase.tasks[taskIndex] = {
            ...phase.tasks[taskIndex],
            ...updates
          };
          state.isDraft = true;
        }
      }
    },

    deleteTaskLocal: (state, action) => {
      const { phaseId, taskId } = action.payload;
      const phase = state.project.phases.find(p => p.id === phaseId);
      if (phase) {
        phase.tasks = phase.tasks.filter(t => t.id !== taskId);
        state.isDraft = true;
      }
    },
    removeProjectPhase: (state, action) => {
      const id = action.payload;
      state.project.phases = state.project.phases.filter(p => p.id !== id);
      state.isDraft = true;
    },

    // Task assignment
    assignTaskMember: (state, action) => {
      const { phaseId, taskId, memberId } = action.payload;
      const phase = state.project.phases.find(p => p.id === phaseId);
      if (phase) {
        const task = phase.tasks.find(t => t.id === taskId);
        if (task) {
          if (!task.assignedTo) task.assignedTo = [];
          // Toggle assignment
          if (task.assignedTo.includes(memberId)) {
            task.assignedTo = task.assignedTo.filter(id => id !== memberId);
          } else {
            task.assignedTo.push(memberId);
          }
          state.isDraft = true;
        }
      }
    },

    updateTaskStatus: (state, action) => {
      const { phaseId, taskId, status } = action.payload;
      const phase = state.project.phases.find(p => p.id === phaseId);
      if (phase) {
        const task = phase.tasks.find(t => t.id === taskId);
        if (task) {
          task.status = status;
          state.isDraft = true;
        }
      }
    },

    updateTaskDueDate: (state, action) => {
      const { phaseId, taskId, date } = action.payload;
      const phase = state.project.phases.find(p => p.id === phaseId);
      if (phase) {
        const task = phase.tasks.find(t => t.id === taskId);
        if (task) {
          task.dueDate = date;
          state.isDraft = true;
        }
      }
    },

    // Task comments
    addTaskComment: (state, action) => {
      const { phaseId, taskId, comment } = action.payload;
      const phase = state.project.phases.find(p => p.id === phaseId);
      if (phase) {
        const task = phase.tasks.find(t => t.id === taskId);
        if (task) {
          if (!task.comments) task.comments = [];
          task.comments.push(comment);
          state.isDraft = true;
        }
      }
    },

    removeTaskComment: (state, action) => {
      const { phaseId, taskId, commentId } = action.payload;
      const phase = state.project.phases.find(p => p.id === phaseId);
      if (phase) {
        const task = phase.tasks.find(t => t.id === taskId);
        if (task && task.comments) {
          task.comments = task.comments.filter(c => c.id !== commentId);
          state.isDraft = true;
        }
      }
    },

    toggleTaskCommentLike: (state, action) => {
      const { phaseId, taskId, commentId, userId } = action.payload;
      const phase = state.project.phases.find(p => p.id === phaseId);
      if (phase) {
        const task = phase.tasks.find(t => t.id === taskId);
        if (task && task.comments) {
          const comment = task.comments.find(c => c.id === commentId);
          if (comment) {
            if (!comment.likes) comment.likes = [];
            const index = comment.likes.indexOf(userId);
            if (index === -1) {
              comment.likes.push(userId);
            } else {
              comment.likes.splice(index, 1);
            }
            state.isDraft = true;
          }
        }
      }
    },

    // Team management actions
    addTeamMember: (state, action) => {
      const member = action.payload;
      // Don't add if already exists
      if (!state.teamMembers.some(m => m.id === member.id)) {
        state.teamMembers.push(member);
        state.isDraft = true;

        // Add to recently added for UI feedback
        if (!state.userSearch.recentlyAdded.includes(member.id)) {
          state.userSearch.recentlyAdded.push(member.id);
        }
      }
    },

    removeTeamMember: (state, action) => {
      const memberId = action.payload;
      state.teamMembers = state.teamMembers.filter(m => m.id !== memberId);
      state.isDraft = true;
    },

    updateTeamMemberRole: (state, action) => {
      const { memberId, role } = action.payload;
      const member = state.teamMembers.find(m => m.id === memberId);
      if (member) {
        member.role = role;
        state.isDraft = true;
      }
    },

    setTeamMembers: (state, action) => {
      state.teamMembers = action.payload;
      state.isDraft = true;
    },

    // User search actions
    setUserSearchQuery: (state, action) => {
      state.userSearch.query = action.payload;
    },

    setUserSearchResults: (state, action) => {
      state.userSearch.results = action.payload;
    },

    setUserSearching: (state, action) => {
      state.userSearch.isSearching = action.payload;
    },

    setShowUserSearchDropdown: (state, action) => {
      state.userSearch.showDropdown = action.payload;
    },

    clearRecentlyAdded: (state, action) => {
      const memberId = action.payload;
      state.userSearch.recentlyAdded = state.userSearch.recentlyAdded.filter(id => id !== memberId);
    },

    // Location search actions
    updateLocationSearch: (state, action) => {
      state.locationSearch = { ...state.locationSearch, ...action.payload };
    },

    setLocationSearchTerm: (state, action) => {
      state.locationSearch.searchTerm = action.payload;
    },

    setSelectedLocation: (state, action) => {
      state.locationSearch.selectedLocation = action.payload;
      if (action.payload) {
        // Standardize to full selected location object in postData
        state.postData.location = action.payload;
        state.isDraft = true;
      }
    },

    clearSelectedLocation: (state) => {
      state.locationSearch.selectedLocation = null;
      state.locationSearch.searchTerm = '';
      state.postData.location = null;
      state.isDraft = true;
    },

    // UI state actions
    toggleDropdown: (state, action) => {
      const field = action.payload;
      state.ui.dropdownOpen[field] = !state.ui.dropdownOpen[field];
    },

    setDropdownOpen: (state, action) => {
      const { field, open } = action.payload;
      state.ui.dropdownOpen[field] = open;
    },

    setFirestoreDraftId: (state, action) => {
      state.firestoreDraftId = action.payload;
    },

    // Sync draft data from Firestore (for real-time updates)
    syncDraftFromFirestore: (state, action) => {
      const data = action.payload;
      if (!data) return;

      // Update project fields
      if (data.title) state.project.projectTitle = data.title;
      if (data.mission) state.project.mission = data.mission;
      if (data.imageUrl) state.project.imageUrl = data.imageUrl;
      if (data.solutionType) state.project.solutionType = data.solutionType;
      if (data.sections) state.project.sections = data.sections;
      if (data.phases) state.project.phases = data.phases;

      // Update post data fields
      if (data.issue) state.postData.issue = data.issue;
      if (data.scale) state.postData.scale = data.scale;
      if (data.location) state.postData.location = data.location;

      // Update team members
      if (data.teamMembers) state.teamMembers = data.teamMembers;

      // Update source data if exists
      if (data.sourceSolutionId) {
        state.project.sourceData.sourceSolutionId = data.sourceSolutionId;
      }
    },

    setConfirmDialog: (state, action) => {
      state.ui.confirmDialog = action.payload;
    },

    setCurrentStep: (state, action) => {
      state.ui.currentStep = action.payload;
    },

    // Draft management
    markDraftSaved: (state) => {
      state.isDraft = false;
      state.lastSaved = new Date().toISOString();
    },

    clearDraft: (state) => {
      return { ...initialState };
    },

    // Set project source for path tracking
    setProjectSource: (state, action) => {
      const { sourceType, sourceData } = action.payload;
      state.project.sourceType = sourceType;
      state.project.sourceData = { ...state.project.sourceData, ...sourceData };
    },

    // Set project mission for FromPost path
    setProjectMission: (state, action) => {
      const { projectMission, originalPostId, selectedSolutionId } = action.payload;
      state.project.sourceType = 'post';
      state.project.sourceData.projectMission = projectMission;
      state.project.sourceData.originalPostId = originalPostId;
      state.project.sourceData.selectedSolutionId = selectedSolutionId;
    },

    // Set implementation plan for FromSolution path
    setImplementationPlan: (state, action) => {
      const { implementationPlan, sourceSolutionId } = action.payload;
      state.project.sourceType = 'solution';
      state.project.sourceData.implementationPlan = implementationPlan;
      state.project.sourceData.sourceSolutionId = sourceSolutionId;
    },

    resetToStep: (state, action) => {
      state.ui.currentStep = action.payload;
      // Clear step-specific temporary state
      if (action.payload === 'post') {
        state.userSearch = initialState.userSearch;
        state.ui.confirmDialog = null;
      }
    },

    // Initialize draft with current user as team lead
    initializeDraft: (state, action) => {
      const { currentUser, userData } = action.payload;

      if (currentUser && state.teamMembers.length === 0) {
        const userMember = {
          id: currentUser.uid,
          username: getDisplayName({
            profile: userData || null,
            authUser: currentUser || null,
            candidates: [
              userData?.username,
              userData?.displayName,
              userData?.name,
              currentUser?.displayName
            ]
          }),
          profession: userData?.profession || currentUser.profession || 'Team Lead',
          role: 'Team Lead',
          photoURL: userData?.photoURL || currentUser.photoURL,
          isCurrentUser: true
        };
        state.teamMembers = [userMember];
      }
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(saveDraftToStorage.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveDraftToStorage.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.lastSaved = new Date().toISOString();
        }
      })
      .addCase(saveDraftToStorage.rejected, (state, action) => {
        state.loading = false;
        state.error = 'Failed to save draft';
      })
      .addCase(loadDraftFromStorage.fulfilled, (state, action) => {
        if (action.payload) {
          // Merge loaded state with current state, preserving any existing data
          return {
            ...state,
            ...action.payload,
            loading: false,
            error: null
          };
        }
      })
      .addCase(loadDraftFromStorage.rejected, (state, action) => {
        state.loading = false;
        state.error = 'Failed to load saved draft';
      });
  }
});

export const {
  // Post actions
  updatePostData,
  setPostField,
  // Project builder
  setProjectField,
  addProjectSection,
  updateProjectSection,
  removeProjectSection,
  addProjectPhase,
  updateProjectPhase,
  removeProjectPhase,
  assignTaskMember,
  updateTaskStatus,
  updateTaskDueDate,
  addTaskComment,
  removeTaskComment,
  toggleTaskCommentLike,

  // Team actions
  addTeamMember,
  removeTeamMember,
  updateTeamMemberRole,
  setTeamMembers,

  // User search actions
  setUserSearchQuery,
  setUserSearchResults,
  setUserSearching,
  setShowUserSearchDropdown,
  clearRecentlyAdded,

  // Location actions
  updateLocationSearch,
  setLocationSearchTerm,
  setSelectedLocation,
  clearSelectedLocation,

  // UI actions
  toggleDropdown,
  setDropdownOpen,
  setConfirmDialog,
  setCurrentStep,

  // Draft actions
  markDraftSaved,
  clearDraft,
  resetToStep,
  initializeDraft,
  setFirestoreDraftId,
  syncDraftFromFirestore,

  // Source tracking actions
  setProjectSource,
  setProjectMission,
  setImplementationPlan
} = projectDraftSlice.actions;



// Selectors
export const selectProjectDraft = (state) => state.projectDraft;
export const selectPostData = (state) => state.projectDraft.postData;
export const selectProjectData = (state) => state.projectDraft.project;
export const selectTeamMembers = (state) => state.projectDraft.teamMembers;
export const selectUserSearch = (state) => state.projectDraft.userSearch;
export const selectLocationSearch = (state) => state.projectDraft.locationSearch;
export const selectDraftUI = (state) => state.projectDraft.ui;
export const selectIsDraft = (state) => state.projectDraft.isDraft;
export const selectCurrentStep = (state) => state.projectDraft.ui.currentStep;

// Helper selectors
export const selectIsTeamMember = (state, userId) =>
  state.projectDraft.teamMembers.some(member => member.id === userId);

export const selectIsRecentlyAdded = (state, userId) =>
  state.projectDraft.userSearch.recentlyAdded.includes(userId);

export const selectCanProceed = (state) => {
  const { postData, teamMembers } = state.projectDraft;
  return (
    postData.issue &&
    postData.location &&
    postData.actionTag &&
    postData.content &&
    teamMembers.length > 0
  );
};

// Re-export all thunks for backward compatibility
export {
  saveDraftToStorage,
  loadDraftFromStorage,
  submitTaskComment,
  toggleCommentLike,
  updateTaskThunk,
  deleteTaskThunk,
};

export default projectDraftSlice.reducer;
